import React, { useEffect, useState, useCallback } from 'react';
import ReactFlow, { MiniMap, Controls, Background, applyNodeChanges, applyEdgeChanges } from 'reactflow';
import 'reactflow/dist/style.css';
import { fetchSteps, updateStep, addStep, deleteStep } from '../api/steps';
import StepForm from './StepForm';
import NewStepForm from './NewStepForm';
import LabelForm from './LabelForm';
import { Button, Alert, Modal, Form } from 'react-bootstrap';

export default function FlowEditor() {
  const [steps, setSteps] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedStep, setSelectedStep] = useState(null);
  const [showNewStepForm, setShowNewStepForm] = useState(false);
  const [newStepParent, setNewStepParent] = useState(null);
  const [showLabelForm, setShowLabelForm] = useState(false);
  const [pendingSource, setPendingSource] = useState(null);
  const [pendingTarget, setPendingTarget] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('');
  const [showStep3Form, setShowStep3Form] = useState(false);
  const [showStep9Form, setShowStep9Form] = useState(false);

  // Save node positions to database
  const savePositionsToDb = useCallback(
    debounce(async (nodesToSave) => {
      try {
        const positionsToUpdate = [];
        nodesToSave.forEach(node => {
          const step = steps.find(s => s._id === node.id);
          if (step) {
            // Always update if position has changed
            const hasChanged = !step.position || 
                             step.position.x !== node.position.x || 
                             step.position.y !== node.position.y;
            
            if (hasChanged) {
              positionsToUpdate.push({ 
                stepId: node.id, 
                position: { x: node.position.x, y: node.position.y }
              });
            }
          }
        });

        // Update positions in database
        for (const update of positionsToUpdate) {
          const step = steps.find(s => s._id === update.stepId);
          if (step) {
            const updatedStep = {
              ...step,
              description: step.description || { en: '', fr: '' },
              position: update.position,
              options: step.options || {}
            };
            
            await updateStep(update.stepId, updatedStep);
            console.log(`Position saved for step ${update.stepId}:`, update.position);
          }
        }

        // Update local state
        if (positionsToUpdate.length > 0) {
          setSteps(prevSteps =>
            prevSteps.map(step => {
              const update = positionsToUpdate.find(u => u.stepId === step._id);
              return update ? { ...step, position: update.position } : step;
            })
          );
        }
      } catch (err) {
        console.error('Failed to save positions to database:', err);
      }
    }, 1000),
    [steps]
  );

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  const getNextAvailablePosition = (existingNodes) => {
    const gridSpacingX = 350;
    const gridSpacingY = 180;
    const occupiedPositions = new Set();
    existingNodes.forEach(node => {
      occupiedPositions.add(`${node.position.x},${node.position.y}`);
    });
    let row = 0;
    let col = 0;
    while (true) {
      const x = col * gridSpacingX + 50;
      const y = row * gridSpacingY + 50;
      const posKey = `${x},${y}`;
      if (!occupiedPositions.has(posKey)) {
        return { x, y };
      }
      col++;
      if (col >= 3) {
        col = 0;
        row++;
      }
    }
  };

  useEffect(() => {
    loadSteps();
  }, []);

  const loadSteps = async () => {
    try {
      const res = await fetchSteps();
      let loadedSteps = res.data || [];
      loadedSteps = loadedSteps.map(step => ({
        ...step,
        description: { en: step.description?.en || '', fr: step.description?.fr || '' },
        options: step.options || {},
        position: step.position || null
      }));
      if (loadedSteps.length > 1) {
        loadedSteps = loadedSteps.map((step, index) => {
          if (index < loadedSteps.length - 1) {
            return {
              ...step,
              options: { ...step.options, next: loadedSteps[index + 1]._id }
            };
          }
          return step;
        });
      }
      setSteps(loadedSteps);

      const gridSpacingX = 350;
      const gridSpacingY = 180;
      const newNodes = loadedSteps.map((step, idx) => {
        // Use saved position if available, otherwise calculate default position
        const savedPosition = step.position && typeof step.position === 'object' && 
                             step.position.x !== undefined && step.position.y !== undefined;
        
        const defaultPosition = { 
          x: (idx % 3) * gridSpacingX + 50, 
          y: Math.floor(idx / 3) * gridSpacingY + 50 
        };

        return {
          id: step._id,
          type: 'default',
          data: { label: step.description?.en || step.description?.fr || 'Unnamed Step' },
          style: {
            padding: '24px 28px',
            background: 'linear-gradient(145deg, #fff 0%, #f8fafc 100%)',
            border: '2px solid #e2e8f0',
            borderRadius: '24px',
            minWidth: 320,
            maxWidth: 400,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            fontSize: 16,
            fontWeight: 500,
            color: '#1e293b',
            whiteSpace: 'pre-line',
            lineHeight: 1.5,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            backdropFilter: 'blur(10px)'
          },
          position: savedPosition ? step.position : defaultPosition,
        };
      });

      const newEdges = [];
      loadedSteps.forEach(step => {
        if (step.options && typeof step.options === 'object') {
          Object.entries(step.options).forEach(([label, target]) => {
            if (loadedSteps.some(s => s._id === target)) {
              newEdges.push({
                id: `${step._id}-${target}-${label}`,
                source: step._id,
                target,
                label,
                animated: true,
                style: { stroke: '#3b82f6', strokeWidth: 2.5 },
                labelStyle: {
                  fill: '#1e293b',
                  fontWeight: 600,
                  fontSize: 14,
                  fontFamily: "Inter, -apple-system, sans-serif",
                  background: 'rgba(255,255,255,0.9)',
                  padding: '4px 8px',
                  borderRadius: 8,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                },
                markerEnd: {
                  type: 'arrowclosed',
                  color: '#3b82f6',
                  width: 20,
                  height: 20
                }
              });
            }
          });
        }
      });

      setNodes(newNodes);
      setEdges(newEdges);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load steps. Please try again.');
      console.error('Load steps error:', err);
    }
  };

  const hasCycle = useCallback((source, target, steps) => {
    // (Cycle check not implemented, could be added for advanced edge safety)
    return false;
  }, []);

  const onConnect = async (params) => {
    const { source, target } = params;
    if (source === target) {
      setError('Cannot connect a step to itself.');
      return;
    }
    const sourceStep = steps.find(s => s._id === source);
    const targetStep = steps.find(s => s._id === target);
    if (!sourceStep || !targetStep) {
      setError('Source or target step not found.');
      return;
    }
    if (sourceStep.options && typeof sourceStep.options === 'object') {
      const existingTargets = Object.values(sourceStep.options);
      if (existingTargets.includes(target)) {
        setError('Already connected to this step.');
        return;
      }
    }
    setPendingSource(source);
    setPendingTarget(target);
    setConnectionStatus('Connection started. Enter label...');
    setShowLabelForm(true);
  };

  const handleSaveLabel = async (label) => {
    if (!label.trim()) {
      setError('Label is required.');
      return;
    }
    const sourceStep = steps.find(s => s._id === pendingSource);
    try {
      const updatedOptions = { ...sourceStep.options, [label]: pendingTarget };
      await updateStep(pendingSource, {
        ...sourceStep,
        description: sourceStep.description || { en: '', fr: '' },
        options: updatedOptions
      });
      const newEdge = {
        id: `${pendingSource}-${pendingTarget}-${label}`,
        source: pendingSource,
        target: pendingTarget,
        label,
        animated: true,
        style: { stroke: '#3b82f6', strokeWidth: 2.5 },
        labelStyle: { fill: '#1e293b', fontWeight: 600, fontSize: 14 },
        markerEnd: { type: 'arrowclosed', color: '#3b82f6', width: 20, height: 20 }
      };
      setEdges(prevEdges => [...prevEdges, newEdge]);
      setSteps(prevSteps => prevSteps.map(s =>
        s._id === pendingSource ? { ...s, options: updatedOptions } : s
      ));
      setShowLabelForm(false);
      setPendingSource(null);
      setPendingTarget(null);
      setSuccessMessage('Connection added successfully!');
      setError('');
      setConnectionStatus('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Save label error:', err);
      setError('Failed to connect steps.');
    }
  };

  const onEdgeDelete = async (edgeId) => {
    if (!window.confirm('Are you sure you want to delete this connection?')) return;
    const edgeParts = edgeId.split('-');
    let source, target, label;
    if (edgeParts.length >= 3) {
      source = edgeParts[0];
      target = edgeParts[1];
      label = edgeParts.slice(2).join('-').replace(/-\d+$/, '');
    } else {
      setError('Invalid edge format for deletion.');
      return;
    }
    const sourceStep = steps.find(s => s._id === source);
    if (!sourceStep || !sourceStep.options || !sourceStep.options[label]) {
      setError('Connection not found.');
      return;
    }
    try {
      const updatedOptions = { ...sourceStep.options };
      delete updatedOptions[label];
      await updateStep(sourceStep._id, {
        ...sourceStep,
        description: sourceStep.description || { en: '', fr: '' },
        options: Object.keys(updatedOptions).length > 0 ? updatedOptions : {}
      });
      setEdges(prevEdges => prevEdges.filter(e => e.id !== edgeId));
      setSteps(prevSteps => prevSteps.map(s =>
        s._id === source ? { ...s, options: updatedOptions } : s
      ));
      setSuccessMessage('Connection deleted successfully!');
      setError('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Delete edge error:', err);
      setError('Failed to delete connection.');
    }
  };

  const onDeleteStep = async (stepId) => {
    if (!window.confirm('Are you sure you want to delete this step?')) return;
    try {
      await deleteStep(stepId);
      const updatedSteps = steps.map(s => {
        if (s.options) {
          const updatedOptions = { ...s.options };
          Object.keys(updatedOptions).forEach(key => {
            if (updatedOptions[key] === stepId) {
              delete updatedOptions[key];
            }
          });
          return { ...s, options: Object.keys(updatedOptions).length ? updatedOptions : {} };
        }
        return s;
      }).filter(s => s._id !== stepId);
      setSteps(updatedSteps);
      setNodes(nodes => nodes.filter(n => n.id !== stepId));
      setEdges(edges => edges.filter(e => e.source !== stepId && e.target !== stepId));
      setSelectedStep(null);
      setSuccessMessage('Step deleted successfully!');
      setError('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Delete step error:', err);
      setError('Failed to delete step.');
    }
  };

  const openAddNewStepForm = (parentStepId = null) => {
    setNewStepParent(parentStepId);
    setShowNewStepForm(true);
  };

  const handleAddNewStep = async (newStepData) => {
    try {
      const nextPosition = getNextAvailablePosition(nodes);
      const res = await addStep({
        ...newStepData,
        description: newStepData.description || { en: '', fr: '' },
        options: {},
        position: nextPosition
      });
      const newStep = { ...res.data, position: nextPosition };
      setSteps(prev => [...prev, newStep]);
      const newNode = {
        id: newStep._id,
        type: 'default',
        data: { label: newStep.description?.en || 'Unnamed Step' },
        style: {
          padding: '24px 28px',
          background: 'linear-gradient(145deg, #fff 0%, #f8fafc 100%)',
          border: '2px solid #e2e8f0',
          borderRadius: '24px',
          minWidth: 320,
          maxWidth: 400,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          fontSize: 16,
          fontWeight: 500,
          color: '#1e293b',
          whiteSpace: 'pre-line',
          lineHeight: 1.5,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          backdropFilter: 'blur(10px)'
        },
        position: nextPosition,
      };
      setNodes(prev => [...prev, newNode]);
      if (newStepParent) {
        setPendingSource(newStepParent);
        setPendingTarget(newStep._id);
        setShowNewStepForm(false);
        setShowLabelForm(true);
      } else {
        setShowNewStepForm(false);
        setNewStepParent(null);
        setSuccessMessage('Step added successfully!');
        setError('');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      console.error('Add step error:', err);
      setError('Failed to add new step.');
    }
  };
  
  const onNodeClick = (event, node) => {
    const clickedStep = steps.find(s => s._id === node.id);
    if (clickedStep) {
      setSelectedStep(clickedStep);
      if (clickedStep.description?.en === 'Step 3') {
        setShowStep3Form(true);
      } else if (clickedStep._id === '68c3b79fefc724e2fde4e7fd') {
        setShowStep9Form(true);
      }
    }
  };

  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const updatedNodes = applyNodeChanges(changes, nds);
      const positionChanges = changes.filter(change => 
        change.type === 'position' && change.dragging === false
      );
      if (positionChanges.length > 0) {
        savePositionsToDb(updatedNodes);
      }
      return updatedNodes;
    });
  }, [savePositionsToDb]);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

 const Step3Form = () => {
    const [formData, setFormData] = useState({ condition: '' });
    const handleSave = () => {
      setSuccessMessage(`Step 3 condition set to: ${formData.condition}`);
      setShowStep3Form(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    };

    return (
      <Modal show={showStep3Form} onHide={() => setShowStep3Form(false)} backdrop="static" centered size="md">
        <Modal.Header closeButton className="border-0 pb-3" style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
          color: 'white',
          borderRadius: '16px 16px 0 0'
        }}>
          <Modal.Title className="fw-bold d-flex align-items-center">
            <div className="me-2 d-flex align-items-center justify-content-center" style={{
              width: '32px',
              height: '32px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '8px'
            }}>
              <i className="bi bi-gear-fill" style={{ fontSize: '16px', color: 'white' }}></i>
            </div>
            Step 3 Configuration
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4" style={{ background: '#f8fafc', borderRadius: '0 0 16px 16px' }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold mb-2 text-muted">Condition Logic</Form.Label>
              <Form.Control
                type="text"
                value={formData.condition}
                onChange={e => setFormData({ ...formData, condition: e.target.value })}
                placeholder="e.g., user.age > 18 && user.plan === 'premium'"
                className="border-0 shadow-sm"
                style={{ 
                  borderRadius: '12px', 
                  padding: '12px 16px',
                  background: 'white',
                  fontSize: '14px'
                }}
              />
              <Form.Text className="text-muted">
                Define the condition for this decision step
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 px-4 pb-4" style={{ background: '#f8fafc', borderRadius: '0 0 16px 16px' }}>
          <div className="d-flex gap-3 w-100 justify-content-end">
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowStep3Form(false)} 
              className="px-4 py-2 fw-semibold border-gray-300"
              style={{ 
                borderRadius: '12px',
                background: 'white',
                color: '#6b7280',
                border: '1px solid #e5e7eb'
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              className="px-4 py-2 fw-semibold border-0 shadow-sm"
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                borderRadius: '12px',
                color: 'white'
              }}
            >
              Save Configuration
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  };

  const Step9Form = () => {
    const [formData, setFormData] = useState({ connectionCheck: '' });
    const handleSave = () => {
      setSuccessMessage(`Step 9 connection verified: ${formData.connectionCheck || 'Complete'}`);
      setShowStep9Form(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    };

    return (
      <Modal show={showStep9Form} onHide={() => setShowStep9Form(false)} backdrop="static" centered size="md">
        <Modal.Header closeButton className="border-0 pb-3" style={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          color: 'white',
          borderRadius: '16px 16px 0 0'
        }}>
          <Modal.Title className="fw-bold d-flex align-items-center">
            <div className="me-2 d-flex align-items-center justify-content-center" style={{
              width: '32px',
              height: '32px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '8px'
            }}>
              <i className="bi bi-diagram-2-fill" style={{ fontSize: '16px', color: 'white' }}></i>
            </div>
            Step 9 Connection Validation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4" style={{ background: '#f8fafc', borderRadius: '0 0 16px 16px' }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold mb-2 text-muted">Connection Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.connectionCheck}
                onChange={e => setFormData({ ...formData, connectionCheck: e.target.value })}
                placeholder="Enter connection verification notes or target step ID..."
                className="border-0 shadow-sm"
                style={{ 
                  borderRadius: '12px', 
                  padding: '12px 16px',
                  background: 'white',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
              <Form.Text className="text-muted">
                Verify that this step connects properly to downstream processes
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 px-4 pb-4" style={{ background: '#f8fafc', borderRadius: '0 0 16px 16px' }}>
          <div className="d-flex gap-3 w-100 justify-content-end">
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowStep9Form(false)} 
              className="px-4 py-2 fw-semibold border-gray-300"
              style={{ 
                borderRadius: '12px',
                background: 'white',
                color: '#6b7280',
                border: '1px solid #e5e7eb'
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              className="px-4 py-2 fw-semibold border-0 shadow-sm"
              style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                borderRadius: '12px',
                color: 'white'
              }}
            >
              Verify Connection
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <div style={{ 
      height: '100vh', 
      width: '100%', 
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Subtle Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `
          radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.2) 0%, transparent 50%)
        `,
        pointerEvents: 'none',
        zIndex: 0
      }} />

      {/* Enhanced Header */}
      <header style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        padding: '0.5rem 1.5rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        {/* Left Section - Logo & Title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {/* Logo */}
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
            color: 'white',
            fontSize: '18px'
          }}>
            <i className="bi bi-diagram-3-fill"></i>
          </div>
          
          {/* Title */}
          <div>
            <h1 style={{ 
              margin: 0, 
              fontSize: '1.25rem', 
              fontWeight: '700',
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.025em'
            }}>
              Workflow Designer
            </h1>
            <p style={{ 
              margin: '0.125rem 0 0 0',
              color: '#64748b',
              fontSize: '0.75rem',
              fontWeight: '500'
            }}>
              Build sophisticated workflow automation
            </p>
          </div>
          
          {/* Stats */}
          <div style={{ display: 'flex', gap: '0.75rem', marginLeft: '1rem' }}>
            {[
              { icon: 'bi-boxes', count: steps.length, label: 'Steps', color: '#3b82f6' },
              { icon: 'bi-arrow-left-right', count: edges.length, label: 'Connections', color: '#10b981' }
            ].map((stat, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0.75rem',
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                border: '1px solid #e5e7eb',
                transition: 'all 0.2s ease',
                cursor: 'default'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = `0 4px 12px ${stat.color}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.06)';
              }}
              >
                <div style={{
                  width: '24px',
                  height: '24px',
                  background: stat.color,
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '12px'
                }}>
                  <i className={stat.icon}></i>
                </div>
                <div>
                  <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>
                    {stat.count}
                  </div>
                  <div style={{ fontSize: '0.65rem', color: '#6b7280', fontWeight: '500' }}>
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Add Step Button */}
        <Button
          onClick={() => openAddNewStepForm()}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            border: 'none',
            borderRadius: '8px',
            padding: '0.5rem 1rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.4)',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.4)';
          }}
        >
          <i className="bi bi-plus-lg"></i>
          Add New Step
        </Button>
      </header>

      {/* Notifications */}
      <div style={{ 
        padding: '0.5rem 1.5rem', 
        position: 'relative', 
        zIndex: 2,
        flexShrink: 0
      }}>
        {error && (
          <Alert 
            variant="danger" 
            style={{ 
              borderRadius: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
              color: '#dc2626',
              boxShadow: '0 4px 12px rgba(220, 38, 38, 0.15)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem'
            }}
          >
            <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '1.25rem' }}></i>
            <strong>Error:</strong> {error}
          </Alert>
        )}
        {successMessage && (
          <Alert 
            variant="success" 
            style={{ 
              borderRadius: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
              color: '#166534',
              boxShadow: '0 4px 12px rgba(22, 101, 52, 0.15)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem'
            }}
          >
            <i className="bi bi-check-circle-fill" style={{ fontSize: '1.25rem' }}></i>
            <strong>Success:</strong> {successMessage}
          </Alert>
        )}
        {connectionStatus && (
          <Alert 
            variant="info" 
            style={{ 
              borderRadius: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
              color: '#1e40af',
              boxShadow: '0 4px 12px rgba(30, 64, 175, 0.15)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem'
            }}
          >
            <i className="bi bi-info-circle-fill" style={{ fontSize: '1.25rem' }}></i>
            <strong>Connection:</strong> {connectionStatus}
          </Alert>
        )}
      </div>

      {/* Main Content Area */}
      <main style={{ 
        flex: 1, 
        padding: '0.5rem 1.5rem 1rem', 
        position: 'relative', 
        zIndex: 2,
        minHeight: 0
      }}>
        <div style={{ 
          height: '100%', 
          borderRadius: '20px',
          overflow: 'hidden',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          position: 'relative'
        }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodeClick={onNodeClick}
            onConnect={onConnect}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            fitView
            fitViewOptions={{ padding: 0.1 }}
            connectOnClick={true}
            onEdgeDoubleClick={(event, edge) => onEdgeDelete(edge.id)}
            style={{ 
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              height: '100%'
            }}
          >
            <Background 
              color="#e2e8f0" 
              gap={20} 
              variant="dots"
              size={1}
            />
            <Controls
              style={{
                position: 'absolute',
                // right: 10,
                // top: 10,/
                background: 'white',
                borderRadius: '12px',
                padding: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb'
              }}
            >
              {({ zoomIn, zoomOut, fitView }) => (
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    onClick={zoomIn}
                    style={{
                      width: '32px',
                      height: '32px',
                      border: 'none',
                      borderRadius: '8px',
                      background: 'white',
                      color: '#374151',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <i className="bi bi-zoom-in"></i>
                  </button>
                  <button
                    onClick={zoomOut}
                    style={{
                      width: '32px',
                      height: '32px',
                      border: 'none',
                      borderRadius: '8px',
                      background: 'white',
                      color: '#374151',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <i className="bi bi-zoom-out"></i>
                  </button>
                  <button
                    onClick={fitView}
                    style={{
                      width: '32px',
                      height: '32px',
                      border: 'none',
                      borderRadius: '8px',
                      background: 'white',
                      color: '#374151',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <i className="bi bi-arrow-up-left-circle"></i>
                  </button>
                </div>
              )}
            </Controls>
            <MiniMap
              style={{
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }}
              maskColor="rgba(102, 126, 234, 0.3)"
            />
          </ReactFlow>
        </div>
      </main>

      {/* Action Bar */}
      <footer style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px) saturate(180%)',
        borderTop: '1px solid rgba(255, 255, 255, 0.2)',
        padding: '0.5rem 1.5rem',
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.08)',
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        {/* <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Button
            onClick={() => openAddNewStepForm()}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              border: 'none',
              borderRadius: '12px',
              padding: '0.75rem 1.5rem',
              fontSize: '0.95rem',
              fontWeight: '600',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 25px rgba(16, 185, 129, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 14px rgba(16, 185, 129, 0.4)';
            }}
          >
            <i className="bi bi-plus-circle"></i>
            New Step
          </Button>
          
          {selectedStep && (
            <>
              <Button
                onClick={() => openAddNewStepForm(selectedStep._id)}
                style={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  boxShadow: '0 4px 14px rgba(245, 158, 11, 0.4)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 25px rgba(245, 158, 11, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 14px rgba(245, 158, 11, 0.4)';
                }}
              >
                <i className="bi bi-arrow-right-circle"></i>
                Add After
              </Button>
              <Button
                onClick={() => onDeleteStep(selectedStep._id)}
                style={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  boxShadow: '0 4px 14px rgba(239, 68, 68, 0.4)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 25px rgba(239, 68, 68, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 14px rgba(239, 68, 68, 0.4)';
                }}
              >
                <i className="bi bi-trash"></i>
                Delete
              </Button>
            </>
          )}
        </div> */}

        {selectedStep && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.5rem 1rem',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '14px'
            }}>
              <i className="bi bi-cursor-fill"></i>
            </div>
            <div>
              <div style={{ 
                fontSize: '0.7rem', 
                color: '#6b7280',
                fontWeight: '500',
                marginBottom: '0.125rem'
              }}>
                Selected Step
              </div>
              <div style={{ 
                fontSize: '0.85rem', 
                color: '#1e293b',
                fontWeight: '600',
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {selectedStep.description?.en?.substring(0, 35) || 'Unnamed Step'}
                {selectedStep.description?.en?.length > 35 ? '...' : ''}
              </div>
            </div>
          </div>
        )}
      </footer>

      {/* Forms */}
      {selectedStep && (
        <StepForm
          step={selectedStep}
          onClose={() => setSelectedStep(null)}
          onSave={async (updated) => {
            try {
              await updateStep(updated._id, {
                ...updated,
                description: updated.description || { en: '', fr: '' }
              });
              setSteps(prevSteps => prevSteps.map(s => s._id === updated._id ? updated : s));
              setNodes(prevNodes => prevNodes.map(n =>
                n.id === updated._id ? { ...n, data: { label: updated.description?.en || '' } } : n
              ));
              setSelectedStep(null);
              setSuccessMessage('Step updated successfully!');
              setError('');
              setTimeout(() => setSuccessMessage(''), 3000);
            } catch (err) {
              console.error('Update step error:', err);
              setError('Failed to update step.');
            }
          }}
        />
      )}

      {showNewStepForm && (
        <NewStepForm
          onClose={() => { setShowNewStepForm(false); setNewStepParent(null); }}
          onSave={handleAddNewStep}
        />
      )}

      {showLabelForm && (
        <LabelForm
          onClose={() => { setShowLabelForm(false); setPendingSource(null); setPendingTarget(null); }}
          onSave={handleSaveLabel}
        />
      )}

      <Step3Form />
      <Step9Form />
    </div>
  );
}
